import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  FileText, Calculator, Send, Users, RefreshCw, Mail,
  CheckCircle, Shield, Clock, Award
} from "lucide-react";

const HomePage = () => {
  const services = [
    { icon: FileText, name: "Individual Tax Returns", desc: "Comprehensive personal tax preparation" },
    { icon: Calculator, name: "Tax Preparation", desc: "Expert handling of all tax documents" },
    { icon: Send, name: "Tax Filing", desc: "Secure electronic filing services" },
    { icon: Users, name: "Tax Consulting", desc: "Strategic tax planning advice" },
  ];

  const stats = [
    { number: "15+", label: "Years Experience" },
    { number: "5000+", label: "Clients Served" },
    { number: "99%", label: "Client Satisfaction" },
    { number: "$2M+", label: "Refunds Secured" },
  ];

  const testimonials = [
    {
      quote: "Goldspot Tax Service made my tax filing experience completely stress-free. Their attention to detail and professionalism is unmatched.",
      author: "Michael R.",
      role: "Small Business Owner"
    },
    {
      quote: "I've been using their services for 5 years now. They always find deductions I would have missed. Highly recommended!",
      author: "Sarah L.",
      role: "Healthcare Professional"
    },
    {
      quote: "The team is incredibly knowledgeable and patient. They explained everything clearly and got me a great refund.",
      author: "James T.",
      role: "IT Consultant"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1761437856311-3ba13025f161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBibGFjayUyMGdvbGQlMjBhYnN0cmFjdCUyMHdhdmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Mjc2NTQ2M3ww&ixlib=rb-4.1.0&q=85"
            alt="Abstract golden design"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="opacity-0 animate-fade-in-up">
                <span className="text-sm uppercase tracking-widest text-[#D4AF37]">
                  Trusted Tax Professionals
                </span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none font-serif opacity-0 animate-fade-in-up stagger-1">
                Expert Tax Services
                <span className="block text-[#D4AF37]">You Can Trust</span>
              </h1>
              
              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-xl opacity-0 animate-fade-in-up stagger-2">
                Maximize your refund with personalized tax preparation and filing services. 
                Our experienced professionals handle your taxes with precision and care.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up stagger-3">
                <a href="tel:+12027042459">
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-8 py-6 text-lg rounded-sm"
                    data-testid="hero-call-btn"
                  >
                    Call Now
                  </Button>
                </a>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-6 text-lg rounded-sm"
                    data-testid="hero-services-btn"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-8 opacity-0 animate-fade-in-up stagger-4">
                {[
                  { icon: Shield, text: "IRS Authorized" },
                  { icon: Award, text: "Licensed CPAs" },
                  { icon: Clock, text: "Walk-ins Welcome" },
                ].map((badge) => (
                  <div key={badge.text} className="flex items-center gap-2 text-muted-foreground">
                    <badge.icon size={18} className="text-[#D4AF37]" />
                    <span className="text-sm">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0A0A0A] border-y border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className="stat-card opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">What We Offer</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
              Our Tax Services
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.name}
                className="service-card opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-semibold mb-3 font-serif">{service.name}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-6 rounded-sm"
                data-testid="view-all-services-btn"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
                Your Financial Success is Our Priority
              </h2>
              <div className="section-divider mt-6 mb-8" />
              
              <div className="space-y-6">
                {[
                  { title: "Expert Tax Professionals", desc: "Our team of licensed CPAs and tax experts bring years of experience to every return." },
                  { title: "Maximum Refund Guarantee", desc: "We work diligently to find every deduction and credit you're entitled to." },
                  { title: "Secure & Confidential", desc: "Your financial information is protected with bank-level security measures." },
                  { title: "Year-Round Support", desc: "Questions don't stop at tax season. We're here for you all year long." },
                ].map((item, index) => (
                  <div key={item.title} className="flex gap-4">
                    <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758520144667-3041caeff3c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwd29ya2luZyUyMG1vZGVybiUyMG9mZmljZSUyMGRhcmslMjB0aGVtZXxlbnwwfHx8fDE3NzI3NjU0NjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional accountant at work"
                className="w-full h-auto object-cover border border-white/5"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] p-6 max-w-xs">
                <p className="text-black font-semibold text-lg">
                  "Trusted by over 5,000 clients for accurate tax services"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
              What Our Clients Say
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="testimonial-card opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`testimonial-${index}`}
              >
                <p className="text-muted-foreground leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-sm text-[#D4AF37]">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1696861273647-92dfe8bb697c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMHNoYWtpbmclMjBoYW5kcyUyMGx1eHVyeSUyMG9mZmljZXxlbnwwfHx8fDE3NzI3NjU0NjV8MA&ixlib=rb-4.1.0&q=85"
            alt="Business partnership"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-serif">
            Ready to Maximize Your Refund?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-2xl mx-auto">
            Call us today or walk in - no appointment needed. Let our experts handle your taxes with care and precision.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <a href="tel:+12027042459">
              <Button 
                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-8 py-6 text-lg rounded-sm"
                data-testid="cta-call-btn"
              >
                Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-6 text-lg rounded-sm"
                data-testid="cta-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
